import { useEffect, useState } from "react";

// Same palette as the public pages, so it looks right there and in the admin app.
const C = {
  blue700: "#1d4ed8", blue600: "#2563eb", blue100: "#e6edfb", blue50: "#f3f6fd",
  green700: "#15803d", green100: "#dcfce7", green50: "#f0fdf4",
  ink900: "#10151f", ink600: "#52596b", ink400: "#8892a6", line: "#d5dbe8",
};

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const CSS = `
.fdz { position: relative; display: flex; align-items: center; gap: 14px; padding: 16px; border-radius: 14px;
  border: 2px dashed ${C.line}; background: ${C.blue50}; transition: border-color .15s, background .15s, box-shadow .15s; box-sizing: border-box; }
.fdz:hover { border-color: ${C.blue600}; background: ${C.blue100}; }
.fdz.fdz-drag { border-color: ${C.blue600}; background: ${C.blue100}; box-shadow: 0 0 0 4px rgba(37,99,235,.12); }
.fdz.fdz-focus { border-color: ${C.blue600}; box-shadow: 0 0 0 3px rgba(37,99,235,.2); }
.fdz.fdz-has { border-style: solid; border-color: #86d9a3; background: ${C.green50}; }
.fdz.fdz-has:hover { border-color: #4cc27a; background: ${C.green50}; }
.fdz.fdz-disabled { opacity: .55; pointer-events: none; }
.fdz input[type="file"] { position: absolute; inset: 0; width: 100%; height: 100%; opacity: 0; cursor: pointer; font-size: 0; }
`;

function UploadIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 14.9A7 7 0 1 1 15.7 8h1.8a4.5 4.5 0 0 1 2.5 8.2" />
      <path d="M12 12v9" />
      <path d="m16 16-4-4-4 4" />
    </svg>
  );
}

function FileIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

/**
 * Drop-in replacement for a bare <input type="file">. `onChange` receives the
 * native change event, exactly as before. Click anywhere on it, or drag a file onto it.
 */
export default function FileDropzone({
  accept,
  required = false,
  disabled = false,
  onChange,
  title = "Tap to choose a file",
  hint = "or drag and drop it here",
}) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [drag, setDrag] = useState(false);
  const [focus, setFocus] = useState(false);

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  function handleChange(e) {
    const f = e.target.files && e.target.files[0];
    if (f) {
      setFile(f);
      setPreview(f.type && f.type.startsWith("image/") ? URL.createObjectURL(f) : null);
    }
    setDrag(false);
    if (onChange) onChange(e);
  }

  const cls = ["fdz", drag && "fdz-drag", focus && "fdz-focus", file && "fdz-has", disabled && "fdz-disabled"]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={cls}
      onDragEnter={() => setDrag(true)}
      onDragOver={() => setDrag(true)}
      onDragLeave={() => setDrag(false)}
      onDrop={() => setDrag(false)}
    >
      <style>{CSS}</style>
      <input
        type="file"
        accept={accept}
        required={required}
        disabled={disabled}
        title=""
        onChange={handleChange}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
      />

      {file ? (
        <>
          {preview ? (
            <img src={preview} alt="" style={{ width: 52, height: 52, borderRadius: 10, objectFit: "cover", flexShrink: 0, border: `1px solid ${C.line}` }} />
          ) : (
            <div style={{ width: 52, height: 52, borderRadius: 10, background: C.green100, color: C.green700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <FileIcon />
            </div>
          )}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 800, color: C.green700, textTransform: "uppercase", letterSpacing: "0.04em" }}>
              <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 16, height: 16, borderRadius: "50%", background: C.green700, color: "#fff" }}>
                <CheckIcon />
              </span>
              Selected
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.ink900, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</div>
            <div style={{ fontSize: 12, color: C.ink400, marginTop: 1 }}>{formatSize(file.size)} · tap to change</div>
          </div>
        </>
      ) : (
        <>
          <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#fff", color: C.blue600, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 1px 3px rgba(16,21,31,0.12)" }}>
            <UploadIcon />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14.5, fontWeight: 700, color: C.ink900 }}>{title}</div>
            <div style={{ fontSize: 12.5, color: C.ink600, marginTop: 2 }}>{hint}</div>
          </div>
        </>
      )}
    </div>
  );
}
